import { Wizard, WizardStep } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { handleResult } from 'modules/core/lib/handleMutation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ActivatePayload } from 'modules/app_store/lib/SubscriptionAddonInstaller'
import subscriptionCollections from 'modules/billing/store/collections/subscriptionAddons'
import { useCreateFirstPayrollPolicy } from 'modules/payroll_bundle/hooks/useCreateFirstPayrollPolicy'
import Policy, { MONTHLY } from 'modules/payroll_bundle/store/models/Policy'

import { PayrollPoliceStep, validatePayrollStep } from './PayrollPoliceStep'
import { UpdatesStep, validateUpdatesStep } from './UpdatesStep'
import { RecurrenceType, WizardData } from './types'

interface PayrollWizardProps {
  onClose: () => void
}

const PayrollWizard = ({ onClose }: PayrollWizardProps) => {
  const { company } = useCurrentSession()
  // FIXME: Stop Ignoring loading state
  const { CreatePayrollPolicy } = useCreateFirstPayrollPolicy()
  const { navigation } = useNavigation()
  const hasProfessionals = hasFeature('factorial_professionals')
  const hasInHousePayroll = hasFeature('inhouse_payroll')
  const skipUpdatesSetup = hasProfessionals || hasInHousePayroll

  const hasNewPayrollSetup = hasFeature('dev_payroll_new_setup')

  const payrollPolicyStep: WizardStep<WizardData> = {
    title: i18n.t('payroll_bundle.onboarding.payroll_policy_step.step_title'),
    nextText: skipUpdatesSetup ? i18n.t('common.submit') : i18n.t('common.next'),
    render: (props) => <PayrollPoliceStep {...props} />,
    isCompleted: (data) => validatePayrollStep(data),
  }

  const updatesStep: WizardStep<WizardData> = {
    title: i18n.t('payroll_bundle.onboarding.employee_updates_step.step_title'),
    nextText: i18n.t('common.submit'),
    render: (props) => <UpdatesStep {...props} />,
    isCompleted: (data) => validateUpdatesStep(data),
  }

  const steps = skipUpdatesSetup ? [payrollPolicyStep] : [payrollPolicyStep, updatesStep]

  const periodicities = Policy.periodicities()
  const defaultRecurrence = periodicities[MONTHLY].recurrence as RecurrenceType

  const payrollSubscription = subscriptionCollections.get('payroll_bundle')

  const activatePayroll = async (payload: ActivatePayload | null | undefined = null) => {
    if (!payrollSubscription) return

    track(trackingEvents.payroll.setupPayroll, {
      app_name: payrollSubscription?.id,
      app_page_path: location.pathname,
      app_page_url: window.location.href,
    })

    // eslint-disable-next-line no-type-assertion/no-type-assertion
    return await asyncAction(() => payrollSubscription.rpc('activate', payload as object))
  }

  return (
    <Wizard.Modal
      onClose={onClose}
      size="large"
      steps={steps}
      initialData={{
        name: 'Default',
        periodicity: MONTHLY,
        enabled_updates: {},
        employees_selected: [],
        country: '',
        currency: '',
        recurrence: defaultRecurrence,
        starts_on: '',
      }}
      onSubmit={async (data) => {
        const employees = data.employees_selected.map(String)
        const updates = Object.keys(data.enabled_updates).filter((key) => data.enabled_updates[key])

        const result = await CreatePayrollPolicy({
          policyWithEmployees: {
            policy: {
              name: data.name,
              default: true,
              companyId: company.id,
              recurrence: data.recurrence,
              startsOn: data.starts_on,
              currency: data.currency,
              country: data.country,
            },
            employeeIds: employees,
          },
          incidenceOptions: updates,
        })

        const handler = handleResult(result, () => null)

        if (handler.success) {
          const payrollPolicyId = handler.data?.onboarding?.id

          if (payrollPolicyId) {
            if (!hasNewPayrollSetup) {
              await activatePayroll({
                bookkeeper_id: undefined,
                bookkeeper_ids: [],
                country: '',
                payroll_policy_id: payrollPolicyId,
                add_employees_to_policy: false,
              })
            }

            track(trackingEvents.payroll.userOnboardingCompleted, {
              company_id: company.id,
              periodicity: data.periodicity,
              currency: data.currency,
              country: data.country,
              update_events: updates,
            })

            app.setFlash({
              type: 'correct',
              message: i18n.t('payroll_bundle.onboarding.correct'),
            })

            // Trigger full page reload to re-fetch feature resolutions
            window.location.href = navigation.payrollBundle.fullPath()
          }
        } else {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('payroll_bundle.onboarding.error'),
          })
        }
      }}
    />
  )
}

export default observer(PayrollWizard)
